/**
 * useScrollToTop — resets window scroll to (0,0) whenever the route's
 * pathname changes. Mount inside a Router context (in each shell wrapper
 * since some routes mount outside a shared shell, e.g. auth + fault routes).
 *
 * Why a hook + manual mount rather than React Router v6's <ScrollRestoration />:
 * ScrollRestoration requires the data-router APIs (createBrowserRouter); the
 * app currently uses the classic <BrowserRouter> + <Routes> tree. Adding a
 * tiny pathname effect is zero-dep and matches the existing pattern.
 *
 * Behavior: scrolls the window, not individual scrollable children. Pages
 * that need to preserve in-page scroll across query-string changes (e.g.,
 * filter changes inside a list page) should opt-out by not using this hook
 * for query-only updates — the effect intentionally fires on full pathname
 * change only, NOT on `search` / `hash` changes.
 *
 * Sub-agent LLLLL (2026-07-29) — anti-punt for Push 102 Track 5 deferral.
 * When UserShell / AdminShell redirect to /sign-in?expired=1&next=<path>,
 * they stash the current window.scrollY into sessionStorage under
 * scrollStorageKey(path). On successful sign-in the user lands back at
 * <path> and this hook restores that scroll offset (with a small
 * rAF-delay so the page has painted at least once). One-shot: the key
 * is removed after read so a later plain navigation doesn't accidentally
 * inherit an old offset.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Sub-agent LLLLL (2026-07-29) — scoped key so the reauth-restore path
 * doesn't collide with any future scroll-position feature. `path` is the
 * pathname + search combo saved at redirect time.
 */
export function scrollStorageKey(path: string): string {
  return `olytic:scroll:reauth:${path}`;
}

export function useScrollToTop(): void {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Sub-agent LLLLL — restore-then-clear when returning from reauth.
    // Best-effort: sessionStorage may throw in private-browsing modes.
    const key = scrollStorageKey(pathname + search);
    let savedY: number | null = null;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw !== null) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) {
          savedY = parsed;
        }
        sessionStorage.removeItem(key);
      }
    } catch {
      /* private browsing / disabled storage — fall through to default */
    }
    if (savedY !== null) {
      // Two-frame delay: first frame lets React render initial content,
      // second frame lets any suspense boundary hydrate. Falls back to
      // an immediate scroll if requestAnimationFrame isn't available.
      const y = savedY;
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, y);
          });
        });
      } else {
        window.scrollTo(0, y);
      }
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, search]);
}

/**
 * Convenience component — mount once inside a Router subtree to apply the
 * scroll reset to every descendant route. Renders nothing.
 */
export function ScrollToTop(): null {
  useScrollToTop();
  return null;
}
